import React, { FC, useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormHelperText,
  Paper,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
import JalaliUtils from '@date-io/jalaali';
import { useForm, Controller } from 'react-hook-form';
import List from './components/errorList';

import Client from 'client-sdk/src/client';
import { GetAllWarehousesRequestBuilder } from 'client-sdk/src/warehouses/warehouses';
import { GetAllCentersRequestBuilder } from 'client-sdk/src/centers/centers';
import { UploadOrdersRequestBuilder } from 'client-sdk/src/orders/orders';

import Icon from '@mdi/react';
import { mdiPaperclip } from '@mdi/js';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotfication';
import { disablePrevDateByHour, handleErrorList, checkHourisPast, convertPersianDigit2English } from '../../utils/helpers';
import AuthContext from '../../authContext';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import ordersRepository from 'client-sdk/src/Repository/orders-repository';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexWrap: 'wrap',
      '& .MuiFormControl-marginNormal': {
        marginTop: 0,
      },
    },
    '.margin-date-0': {
      '&.MuiFormControl-marginNormal': {
        marginTop: 0,
      },
    },
    type: {
      flexGrow: 1,
      maxWidth: 500,
    },
    paper: {
      marginTop: '36px',
      width: '100%',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
    },
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.success.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    headerBox: {
      marginLeft: '75px',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      marginTop: '10px',
    },
    typographyCards: {
      fontSize: '16px',
      color: 'white',
      backgroundColor: '#4BCF90',
      padding: '8px 24px 8px 50px',
      marginLeft: '-40px',
    },
    card: {
      marginBottom: '14px',
      overflow: 'visible',
      padding: '14px',
      paddingBottom: '0',
    },

    cardContent: {
      justifyContent: 'space-between',
      paddingLeft: '14px',
    },
    cardContentOrder: {
      paddingBottom: '10px',
    },
    labelInput: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      padding: '15px',
      marginTop: '16px',
      borderRadius: '5px',
      border: 'solid 1px #949494',
      borderStyle: 'dashed',
      width: '100%',
      height: '150px',
    },
    uploadInput: {
      cursor: 'pointer',
      alignSelf: 'center',
      position: 'absolute',
      top: '0',
      right: '0',
      width: '100%',
      height: '100%',
      opacity: '0',
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textInput: {
      '& input, .MuiSelect-root': {
        padding: '12px 10px 12px !important',
      },

      '& .MuiInput-underline::before': {
        borderBottom: '1px solid #d8d8d8 !important',
      },
    },

    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontWeight: 500,
      width: '100%',
    },
    icon: {
      color: '#8a8a8a',
      marginRight: theme.spacing(1),
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8a8a8a',
      marginRight: '8px',
      marginTop: '21px',
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

interface Props {
  history: any;
}

interface Inputs {
  excelType: string;
  orderType: string;
  deliveryDate: string | null;
  deliveryTime: string | null;
  origin: string;
  destination: string;
  file: null | string;
}

interface Errors {
  code: number;
  text: string;
}

const GroupingCreateOrder: FC<Props> = ({
  history,
}: Props): React.ReactElement => {
  const token = localStorage.getItem('token') || '';
  const authContext = useContext(AuthContext);
  const { register, control, errors, handleSubmit, setValue, getValues, watch } =
    useForm<Inputs>({
      defaultValues: {
        excelType: "RONAD",
        orderType: '',
        origin: '',
        destination: '',
        file: null,
        deliveryDate: null,
        deliveryTime: null
      },
    });
  const [orderTypeState, setOrderType] = useState<null | string>(null);
  const [originOptions, setOriginOptions] = useState<any>([]);
  const [destinations, setDestinations] = useState<any>([]);
  const classes = useStyles();
  const [file, setFile] = useState<any>('');
  const [loading, setLoading] = useState(false as boolean);
  const [errorList, setErrorList] = useState<null | Errors[]>(null);
  const values = getValues()

  useEffect(() => {
    if (values.excelType == "SAZITO") {
      setValue("orderType", "WITHOUT_CENTER")
    }
  }, [values.excelType])
  useEffect(() => {
    const orderType = getValues('orderType');
    setOrderType(orderType);
    setValue('origin', '');
    if (orderType === 'WITHOUT_CENTER' || orderType === 'TO_CENTER') {
      fetchWarehouses().then(res => setOriginOptions(res));
    }

    if (orderType === 'FROM_CENTER') {
      fetchCenters().then(res => {
        setOriginOptions(res);
      });
    }

    if (orderType === 'TO_CENTER') {
      fetchCenters().then(res => setDestinations(res));
    }

    if (orderType !== 'TO_CENTER') {
      setDestinations([]);
    }
  }, [watch('orderType')]);

  useEffect(() => {
    if (authContext.user.role === 'SELLER_ADMIN') {
      setValue('orderType', 'WITHOUT_CENTER');
      setOrderType('WITHOUT_CENTER')
      fetchWarehouses().then(res => setOriginOptions(res));
    }
  }, []);

  const fetchCenters = async () => {
    try {
      const makeRequest = new GetAllCentersRequestBuilder().build();
      const client = new Client(token);
      const { data } = await client.execute(makeRequest);
      return data;
    } catch (err) { }
  };

  const fetchWarehouses = async () => {
    try {
      const makeRequest = new GetAllWarehousesRequestBuilder()
        .params({
          'status.equals': 'ACTIVE',
          size: 100,
        })
        .build();
      const client = new Client(token);
      const { data } = await client.execute(makeRequest);
      return data;
    } catch (err) { }
  };

  const onSubmit = async ({ file, excelType, orderType, origin, destination }: Inputs) => {
    const orderTypeValue = authContext.user.role === 'SELLER_ADMIN' ? orderTypeState : orderType;
    try {
      const handleCenterId = (type: string) => {
        const result = {
          centerId: '',
        };
        if (type === 'FROM_CENTER') {
          result.centerId = origin;
        }
        if (type === 'TO_CENTER') {
          result.centerId = destination;
        }

        if (type === 'WITHOUT_CENTER') {
          result.centerId = '';
        }

        return result;
      };
      setLoading(true);
      let payload = new FormData();
      if (file) {
        payload.append('file', file[0]);
      }
      if (excelType == "RONAD") {
        const makeRequest = new UploadOrdersRequestBuilder()
          .params({
            orderType: orderTypeValue,
            centerId: handleCenterId(orderTypeValue).centerId
              ? handleCenterId(orderTypeValue).centerId
              : undefined,
            fulfillmentWarehouseId:
              orderTypeValue === 'TO_CENTER'
                ? origin
                : orderTypeValue === 'WITHOUT_CENTER'
                  ? origin
                  : undefined,
          })
          .data(payload)
          .build();
        const client = new Client(token);
        await client.execute(makeRequest);
      }
      else {
        // if 
        // (date) {
        //   var deliveryDateVal = null

        // }
        //  else {
        //   var deliveryDateVal = deliveryDate;
        // }
        const response = await ordersRepository.uploadMarketerFile({
          payload: {
            params: {
              centerId: handleCenterId(orderTypeValue).centerId
                ? handleCenterId(orderTypeValue).centerId
                : undefined,
              // deliveryDate: deliveryDateVal,
              fulfillmentWarehouseId:
                orderTypeValue === 'TO_CENTER'
                  ? origin
                  : orderTypeValue === 'WITHOUT_CENTER'
                    ? origin
                    : undefined,
              orderType,
              // deliveryStartTime: convertPersianDigit2English(
              //   deliveryTime.substring(0, 2).concat(':00:00')),
              // deliveryEndTime: convertPersianDigit2English(
              //   deliveryTime.substring(3, 5).concat(':00:00')),
            },
            body: payload,
            type: excelType
          }
        })
      }
      handleNotfication('سفارشات با موفقیت ایجاد شدند', 'success');
      authContext.user.role === 'SELLER_ADMIN' ? history.push('/seller-orders') : history.push('/orders');
    } catch (err) {
      if (err.response?.status === 400) {
        handleNotfication(err.response?.data?.title, 'error');
      }
      if (err?.response.status === 406) {
        // @ts-ignore
        handleNotfication(
          'فرمت فایل اپلود شده صحیح نمیباشد موارد را اصلاح کنید',
          'error'
        );
        // @ts-ignore
        setErrorList(handleErrorList(err.response.data.errors));
      }
      if (
        err.response?.status == 406 &&
        err.response.data.errorKey == 'OrderItem.SkuId.not_acceptable'
      ) {
        err.response.data.errorList.forEach(el => {
          handleNotfication(
            `کالا با کد ${el.productCode} و کدتنوع ${el.variantCode} موجود نیست`,
            'error'
          );
        });
      }
      handleError(err.response);
    } finally {
      setFile(null);
      setValue('file', null);
      setLoading(false);
    }
  };

  const handleDisableTime = (hour: number) => {
    return (
      checkHourisPast(hour, values.deliveryDate)
    );
  };

  const handleDeliveryDateChange = (date: any) => {
    setValue('deliveryTime', '');
  };

  const handleChangeFileInput = (event: any) => {
    setFile(event.target.files[0]);
  };

  return (
    <React.Fragment>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Grid md={12} lg={12} item spacing={2}>
          <Card className={classes.card}>
            <CardHeader
              avatar={
                <Grid>
                  <Typography className={classes.typographyCards}>
                    اطلاعات سفارش
                  </Typography>
                </Grid>
              }
            />

            <CardContent className={classes.cardContentOrder}>
              <Grid container spacing={2}>
                <Grid item md={3} lg={3}>
                  <div style={{ width: '100%' }}>
                    <Controller
                      style={{ width: '100%' }}
                      control={control}
                      name="excelType"
                      label="قالب اکسل"
                      rules={{ required: true }}
                      as={
                        <TextField
                          variant="outlined"
                          select
                          fullWidth
                          disabled={authContext.user.role === 'SELLER_ADMIN'}
                          error={errors.excelType && true}
                        >
                          <MenuItem value={'RONAD'}>
                            روناد
                          </MenuItem>
                          <MenuItem value={'SAZITO'}>
                            سازیتو
                          </MenuItem>
                        </TextField>
                      }
                    />
                    <FormHelperText error focused>
                      {errors.orderType && 'نوع سفارش الزامی میباشد'}
                    </FormHelperText>
                  </div>
                </Grid>
                {authContext.user.role !== 'SELLER_ADMIN' && (
                  <Grid item md={3} lg={3}>
                    <div style={{ width: '100%' }}>
                      <Controller
                        style={{ width: '100%' }}
                        control={control}
                        name="orderType"
                        label="نوع سفارش"
                        rules={{ required: true }}
                        as={
                          <TextField
                            variant="outlined"
                            select
                            fullWidth
                            disabled={authContext.user.role === 'SELLER_ADMIN' || values.excelType == "SAZITO"}
                            error={errors.orderType && true}
                          >
                            <MenuItem value={'WITHOUT_CENTER'}>
                              انبار به مشتری
                            </MenuItem>
                            <MenuItem value={'FROM_CENTER'}>
                              مرکز توزیع به مشتری
                            </MenuItem>
                            <MenuItem value={'TO_CENTER'}>
                              انبار به مرکز توزیع
                            </MenuItem>
                          </TextField>
                        }
                      />
                      <FormHelperText error focused>
                        {errors.orderType && 'نوع سفارش الزامی میباشد'}
                      </FormHelperText>
                    </div>
                  </Grid>
                )}

                <Grid item xs={3} sm={3} md={3} lg={3}>
                  <div className={classes.item}>
                    <div style={{ width: '100%' }} data-cy="origin">
                      <Controller
                        style={{ width: '100%' }}
                        control={control}
                        name="origin"
                        label="مبدا"
                        rules={{ required: true }}
                        as={
                          <TextField
                            variant="outlined"
                            select
                            fullWidth
                            error={errors.origin && true}
                          >
                            {originOptions?.length > 0 ? (
                              originOptions.map((item: any, i: number) => (
                                <MenuItem
                                  data-cy={'origin' + i}
                                  key={item.id}
                                  value={item.id}
                                >
                                  {item.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="">در حال بارگزاری</MenuItem>
                            )}
                          </TextField>
                        }
                      />
                      <FormHelperText error focused>
                        {errors.origin && 'مبدا سفارش الزامی میباشد'}
                      </FormHelperText>
                    </div>
                  </div>
                </Grid>
                {orderTypeState === 'TO_CENTER' && (
                  <Grid container item xs={3} sm={3} md={3} lg={3}>
                    <div className={classes.item}>
                      <div style={{ width: '100%' }} data-cy="destination">
                        <Controller
                          style={{ width: '100%' }}
                          control={control}
                          name="destination"
                          label="مقصد"
                          rules={{ required: orderTypeState === 'TO_CENTER' }}
                          as={
                            <TextField
                              variant="outlined"
                              select
                              fullWidth
                              disabled={orderTypeState !== 'TO_CENTER'}
                              error={errors.origin && true}
                            >
                              {orderTypeState === 'TO_CENTER' &&
                                destinations?.length > 0 ? (
                                destinations.map((item: any, i: number) => (
                                  <MenuItem
                                    data-cy={'dest' + i}
                                    key={item.id}
                                    value={item.id}
                                  >
                                    {item.name}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="">در حال بارگزاری</MenuItem>
                              )}
                            </TextField>
                          }
                        />
                        <FormHelperText error focused>
                          {errors.destination && 'مقصد سفارش الزامی میباشد'}
                        </FormHelperText>
                      </div>
                    </div>
                  </Grid>
                )}
                {/* {watch("excelType") == "SAZITO" && <>
                  <Grid container item xs={12} sm={6} md={3} lg={3}>
                    <div className={classes.item}>
                      <div style={{ width: '100%' }} data-cy="deliveryDatePicker">
                        <MuiPickersUtilsProvider
                          utils={JalaliUtils}
                          locale="fa"
                        >
                          <Controller
                            rules={{ required: true }}
                            name="deliveryDate"
                            control={control}
                            as={
                              <DatePicker
                                inputVariant="outlined"
                                id="deliveryDate"
                                name="deliveryDate"
                                label="تاریخ تحویل سفارش"
                                format="jYYYY/jMM/jDD"
                                minDate={disablePrevDateByHour(16)}
                                fullWidth
                                disablePast
                                autoOk
                                okLabel="تأیید"
                                cancelLabel="لغو"
                                defaultValue={null}
                                value={values.deliveryDate}
                                onChange={handleDeliveryDateChange}
                                error={errors.hasOwnProperty('deliveryDate')}
                                helperText={
                                  errors.deliveryDate &&
                                  errors.deliveryDate.message
                                }
                              />
                            }
                          />
                          <FormHelperText error focused>
                            {errors.deliveryDate &&
                              'تاریخ تحویل سفارش الزامی میباشد'}
                          </FormHelperText>
                        </MuiPickersUtilsProvider>
                      </div>
                    </div>
                  </Grid>
                  <Grid container item xs={12} sm={6} md={3} lg={3}>
                    <div className={classes.item}>
                      <div
                        style={{ width: '100%' }}
                        data-cy="deliveryTimePicker"
                      >
                        <Controller
                          rules={{ required: true }}
                          style={{ width: '100%' }}
                          control={control}
                          name="deliveryTime"
                          label="بازه تحویل سفارش"
                          as={
                            <TextField
                              variant="outlined"
                              select
                              fullWidth
                              // disabled={deliveryType === ''}
                              error={
                                !!errors.deliveryTime && !!errors.deliveryTime
                              }
                            >
                              <MenuItem
                                disabled={handleDisableTime(0)}
                                value={'09-15'}
                                data-cy="firstTime"
                              >
                                {'9 الی 15'}
                              </MenuItem>
                              <MenuItem
                                disabled={handleDisableTime(10)}
                                value={'15-21'}
                                data-cy="secondTime"
                              >
                                {'15 الی 21'}
                              </MenuItem>
                            </TextField>
                          }
                        />
                        <FormHelperText error focused>
                          {errors?.deliveryTime &&
                            ' بازه تحویل سفارش الزامی میباشد'}
                        </FormHelperText>
                      </div>
                    </div>
                  </Grid>
                </>} */}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid md={12} lg={12} item>
          <Card
            className={classes.card}
            style={{ marginTop: '0', paddingBottom: '24px' }}
          >
            <CardHeader
              avatar={
                <Grid>
                  <Typography className={classes.typographyCards}>
                    بارگذاری فایل
                  </Typography>
                </Grid>
              }
            />

            <CardContent className={classes.cardContentOrder}>
              <Grid container lg={12} md={12} className={classes.cardContent}>
                <Grid
                  container
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  style={{
                    padding: '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Grid container sm={5} md={5}>
                    {watch("excelType") == "RONAD" && <Grid
                      item
                      xs={12}
                      lg={12}
                      style={{
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        justifyContent: 'right',
                        textAlign: 'right',
                        marginBottom: '-10px',
                      }}
                    >
                      {orderTypeState === 'FROM_CENTER' ? (<><Link
                        to="/sampleOrderFromCenter.xlsx"
                        style={{
                          color: '#DFA01F',
                          borderBottom: 'solid 1px #DFA01F',
                          paddingBottom: '3px',
                          textDecoration: 'none',
                        }}
                        target="_blank"
                      >
                        {'دریافت فایل نمونه'}
                      </Link>
                        <Link
                          to="/OrdersInstructionfromCenter.xlsx"
                          style={{
                            paddingBottom: '3px',

                            color: '#2F4858',
                            marginLeft: '55px',
                            textDecoration: 'none',
                            borderBottom: 'solid 1px #2F4858',
                          }}
                          target="_blank"
                        >
                          {'دریافت فایل راهنما'}
                        </Link></>) : (<><Link
                          to="/sampleOrder.xlsx"
                          style={{
                            color: '#DFA01F',
                            borderBottom: 'solid 1px #DFA01F',
                            paddingBottom: '3px',
                            textDecoration: 'none',
                          }}
                          target="_blank"
                        >
                          {'دریافت فایل نمونه'}
                        </Link>
                          <Link
                            to="/OrdersInstruction.xlsx"
                            style={{
                              paddingBottom: '3px',

                              color: '#2F4858',
                              marginLeft: '55px',
                              textDecoration: 'none',
                              borderBottom: 'solid 1px #2F4858',
                            }}
                            target="_blank"
                          >
                            {'دریافت فایل راهنما'}
                          </Link></>)}
                    </Grid>}
                    <Grid
                      item
                      xs={12}
                      lg={12}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                      }}
                    >
                      <div style={{ display: 'flex', width: '100%' }}>
                        <div className={classes.item}>
                          {/* <TextField
                            id="amount"
                            name="amount"
                            variant="outlined"
                            className={classes.textInput}
                            style={{padding: '10px 5px', marginTop: '16px'}}
                            value={file ? file?.name : null}
                            placeholder="بارگذاری فایل"
                            disabled
                          /> */}
                          <br />
                          <label htmlFor="file" className={classes.labelInput}>
                            <Icon
                              color={'#666666'}
                              path={mdiPaperclip}
                              size={2}
                            />
                            <Typography>
                              {file ? file?.name : 'فایل خود را اینجا رها کنید'}
                            </Typography>
                            <Typography
                              style={{
                                color: '#0375E8',
                                borderBottom: 'solid 1px #0375E8',
                              }}
                            >
                              انتخاب فایل از کامپیوتر
                            </Typography>

                            <input
                              id="file"
                              type="file"
                              name="file"
                              onChange={handleChangeFileInput}
                              ref={register({ required: true })}
                              multiple
                              className={classes.uploadInput}
                            />
                          </label>
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {errorList && (
          <Grid md={12} lg={12} item>
            <Card
              className={classes.card}
              style={{ padding: '15px 0', marginTop: '0' }}
            >
              <CardHeader
                avatar={
                  <Grid>
                    <Typography className={classes.typographyCards}>
                      لیست خطاها
                    </Typography>
                  </Grid>
                }
              />

              <CardContent className={classes.cardContentOrder}>
                <Grid container lg={12} md={12} className={classes.cardContent}>
                  <Grid container item xs={12} alignItems="flex-end">
                    <div className={classes.item}>
                      <>
                        <List data={errorList} />
                      </>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Grid
          container
          lg={12}
          md={12}
          xs={12}
          spacing={2}
          style={{
            flexDirection: 'row-reverse',
            position: 'fixed',
            backgroundColor: '#D5DADD',
            bottom: '0',
            right: '75px',
            padding: '15px',
            zIndex: 2,
          }}
        >
          <Grid
            container
            style={{
              marginLeft: '35px',
              marginTop: '-4px',
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={() => window.history.back()}
              style={{
                marginRight: 'auto',
                padding: '2px 30px',
              }}
              data-cy="cancelBtn"
            >
              {'انصراف'}
            </Button>
            <Button
              style={{
                marginRight: '14px',
                marginLeft: '90px',
              }}
              type="submit"
              disabled={loading}
              size="medium"
              data-cy="saveBtn"
              autoFocus
              variant="contained"
              color="primary"
            >
              ذخیره سفارش
            </Button>
          </Grid>
        </Grid>
      </form>
      {/* <form onSubmit={handleSubmit(onSubmit)} style={{width: '100%'}}>
        <Grid container style={{margin: '10px 0'}} alignItems="flex-start">
          <Grid container item xs={12} alignItems="flex-start">
            <Grid item container justify="flex-end" xs={12}>
              <Button
                type="submit"
                disabled={loading}
                size="large"
                data-cy="search"
                autoFocus
                variant="contained"
                color="secondary"
                startIcon={<Icon path={mdiPlus} size={1} />}
              >
                ثبت سفارش
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form> */}
    </React.Fragment>
  );
};

export default GroupingCreateOrder;
